// ABOUTME: Orchestration hook for the Order Change Lead Time entity — combines config query, mutations, and form state.
// ABOUTME: Returns a uniform interface consumed by CountryConfigurationsEntity to keep the page component thin.

import { useCallback, useMemo, useRef } from 'react'
import { useOrderChangeLeadTimeConfig } from './useOrderChangeLeadTimeConfig'
import { useCreateOrderChangeLeadTime, useCreateOrderChangeLeadTimeWithScope, useUpdateOrderChangeLeadTime, useDeleteOrderChangeLeadTime } from './useOrderChangeLeadTimeMutations'
import { OrderChangeLeadTimeConfig, OrderChangeLeadTimeFormValues } from '../types/orderChangeLeadTime'
import { EntityActionResult } from '../context/EntityFormContext'
import type { PendingScopeRequest } from '../context/EntityFormContext'
import { getConfigScopeId, toPendingScopeConfig } from '../utils/pendingScope'

interface UseOrderChangeLeadTimeEntityParams {
  countryId: string | undefined
  businessLine: string | undefined
  scopeId: string | undefined
  pendingScope?: PendingScopeRequest | undefined
  rootScopeId?: string | undefined
}

interface EntityActions {
  handleSave: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleReset: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleValuesChange: (values: OrderChangeLeadTimeFormValues) => void
}

interface UseOrderChangeLeadTimeEntityResult {
  config: OrderChangeLeadTimeConfig | undefined
  isLoading: boolean
  error: Error | null
  isSaving: boolean
  actions: EntityActions
}

const equalsParent = (values: OrderChangeLeadTimeFormValues, config: OrderChangeLeadTimeConfig): boolean =>
  JSON.stringify(values.configurations) === JSON.stringify(config.parentValues.configurations)

export const useOrderChangeLeadTimeEntity = ({
  countryId,
  businessLine,
  scopeId,
  pendingScope,
  rootScopeId,
}: UseOrderChangeLeadTimeEntityParams): UseOrderChangeLeadTimeEntityResult => {
  const configScopeId = getConfigScopeId(scopeId, pendingScope, rootScopeId)
  const { data: rawConfig, isLoading, error } = useOrderChangeLeadTimeConfig(countryId, businessLine, configScopeId, rootScopeId)
  const config = useMemo(() => toPendingScopeConfig(rawConfig, pendingScope, (cfg) => ({ configurations: cfg.configurations })), [rawConfig, pendingScope])

  const createMutation = useCreateOrderChangeLeadTime(countryId, businessLine)
  const createWithScopeMutation = useCreateOrderChangeLeadTimeWithScope(countryId, businessLine)
  const updateMutation = useUpdateOrderChangeLeadTime(countryId, businessLine)
  const deleteMutation = useDeleteOrderChangeLeadTime(countryId, businessLine)

  const formValuesRef = useRef<OrderChangeLeadTimeFormValues | null>(null)
  const configIdRef = useRef<string | undefined>(config?.id)
  const mutationsRef = useRef({ createMutation, createWithScopeMutation, updateMutation, deleteMutation })
  const configRef = useRef<OrderChangeLeadTimeConfig | undefined>(config)
  const pendingScopeRef = useRef<PendingScopeRequest | undefined>(pendingScope)

  configIdRef.current = config?.id
  configRef.current = config
  pendingScopeRef.current = pendingScope
  mutationsRef.current = { createMutation, createWithScopeMutation, updateMutation, deleteMutation }

  const handleValuesChange = useCallback((values: OrderChangeLeadTimeFormValues) => {
    formValuesRef.current = values
  }, [])

  const handleSave = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!formValuesRef.current || !configRef.current) return
      const values = formValuesRef.current
      const scopeToCreate = pendingScopeRef.current

      if (scopeToCreate) {
        if (equalsParent(values, configRef.current)) {
          onSuccess({})
          return
        }
        mutationsRef.current.createWithScopeMutation.mutate({ scope: scopeToCreate, values }, { onSuccess: (result) => onSuccess({ wasOverriding: true, createdScopeId: result?.scopeId }), onError: (error) => onError?.(error) })
        return
      }

      if (!scopeId) return

      if (scopeId !== rootScopeId && equalsParent(values, configRef.current)) {
        if (configIdRef.current) {
          mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
        } else {
          onSuccess({ wasDelete: true })
        }
        return
      }

      const isNew = !configIdRef.current
      const mutate = isNew ? mutationsRef.current.createMutation.mutate : mutationsRef.current.updateMutation.mutate
      mutate({ scopeId, values }, { onSuccess: () => onSuccess({ wasOverriding: true }), onError: (error) => onError?.(error) })
    },
    [scopeId, rootScopeId]
  )

  const handleReset = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!scopeId) return
      mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
    },
    [scopeId]
  )

  const actions = useMemo(
    () => ({ handleSave, handleReset, handleValuesChange }),
    [handleSave, handleReset, handleValuesChange]
  )

  const isSaving = createMutation.isPending || createWithScopeMutation.isPending || updateMutation.isPending || deleteMutation.isPending

  return { config, isLoading, error, isSaving, actions }
}
